// =====================================================================
// My Agency V2 — non-SVG graphic primitives (ARTBOARD-SPEC.md §3,
// GLY-01…GLY-09). Exact CSS values from the spec; purely presentational.
//
// GLY-10 (Unicode characters in copy: · — … ’ ‘ “ ” ≤ → *) is a copy
// rule, not a component — surface builders must render those characters
// verbatim inside their strings.
// =====================================================================
import React from "react";
import { MailIcon } from "./icons";

// ── GLY-01 Status dot (Licensed / Unlicensed — internal contexts) ────
export const StatusDot = ({ variant = "licensed", style, ...rest }) => (
  <span
    style={{
      width: 7,
      height: 7,
      borderRadius: "50%",
      flex: "none",
      background: variant === "licensed" ? "#22C55E" : "#F59E0B",
      ...style,
    }}
    {...rest}
  />
);

// Dot + 12px/500 label line, gap 5 (CMP-PILL "Licensed/Unlicensed dot
// line", board 1a family — internal list contexts only; public surfaces
// never render "Unlicensed", see board 2g).
export const StatusDotLine = ({ variant = "licensed", label, ...rest }) => (
  <span className="inline-flex items-center gap-[5px]" {...rest}>
    <StatusDot variant={variant} />
    <span
      className={`text-xs font-medium ${
        variant === "licensed" ? "text-[#15803D]" : "text-[#B45309]"
      }`}
    >
      {label}
    </span>
  </span>
);

// ── GLY-02 / GLY-03 Radio dot (selected / unselected) ────────────────
// Sizes per spec: 15px + 5px border (radio cards), 17px + 5.5px (list
// rows), 16px + 5.5px (superseded mobile 1o).
export const RadioDot = ({ selected = false, size = 15, borderWidth = 5, style, ...rest }) => (
  <span
    style={{
      width: size,
      height: size,
      borderRadius: "50%",
      boxSizing: "border-box",
      flex: "none",
      background: "#fff",
      border: selected ? `${borderWidth}px solid #2283FF` : "1.5px solid #C4CEDA",
      ...style,
    }}
    {...rest}
  />
);

// ── GLY-04 Sheet grabber ─────────────────────────────────────────────
export const SheetGrabber = ({ marginBottom = 14, style, ...rest }) => (
  <div
    style={{
      width: 38,
      height: 4.5,
      borderRadius: 999,
      background: "#DCE3EC",
      margin: `0 auto ${marginBottom}px`,
      ...style,
    }}
    {...rest}
  />
);

// ── GLY-05 Tooltip arrow (8×8 rotated square, position via style) ────
export const TooltipArrow = ({ style, ...rest }) => (
  <span
    style={{
      width: 8,
      height: 8,
      background: "#0F172A",
      transform: "rotate(45deg)",
      position: "absolute",
      left: -4,
      ...style,
    }}
    {...rest}
  />
);

// ── GLY-06 Progress bar (profile completeness) ───────────────────────
// `percent` comes from REAL platform data — never hardcode (Rule 10).
export const ProgressBar = ({ percent = 0, style, ...rest }) => (
  <div
    style={{
      height: 4,
      borderRadius: 999,
      background: "#E7EDF4",
      overflow: "hidden",
      ...style,
    }}
    {...rest}
  >
    <div
      style={{
        width: `${Math.max(0, Math.min(100, percent))}%`,
        height: "100%",
        borderRadius: 999,
        background: "#2283FF",
      }}
    />
  </div>
);

// ── GLY-07 AND/OR divider ────────────────────────────────────────────
export const AndOrDivider = ({ label = "AND / OR", mobile = false, ...rest }) => (
  <div
    className="flex items-center gap-[10px]"
    style={{ margin: mobile ? "10px 0" : "11px 0" }}
    {...rest}
  >
    <span className="flex-1 h-px bg-[#EDF1F6]" />
    <span className="text-[11px] font-semibold tracking-[.04em] text-[#94A3B8]">{label}</span>
    <span className="flex-1 h-px bg-[#EDF1F6]" />
  </div>
);

// ── GLY-08 Menu divider (popover) + sheet section gap ────────────────
export const MenuDivider = (props) => (
  <div style={{ height: 1, background: "#EDF1F6", margin: "6px 4px" }} {...props} />
);

export const SheetSectionGap = (props) => (
  <div
    style={{
      height: 8,
      background: "#F4F6FA",
      margin: "4px -16px",
      borderTop: "1px solid #EDF1F6",
      borderBottom: "1px solid #EDF1F6",
    }}
    {...props}
  />
);

// ── GLY-09 Dashed pending avatar (32px circle + SYM-16 at 13px) ──────
export const DashedPendingAvatar = ({ size = 32, iconSize = 13, ...rest }) => (
  <span
    className="flex items-center justify-center flex-none"
    style={{ width: size, height: size, borderRadius: "50%", border: "1.5px dashed #C4CEDA" }}
    {...rest}
  >
    <MailIcon size={iconSize} />
  </span>
);
